import axios from "axios";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaEnvelope, FaEye, FaEyeSlash, FaLock, FaWhatsapp } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

import LanguageSwitcher from "../components/LanguageSwitcher";
import { useAuth } from "../context/AuthContext";

export default function Login() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  const [sendActivationEmail, setShowSendActivationEmail] = useState(false);
  const [sendingActivationEmail, setSendingActivationEmail] = useState(false);
  const [activationEmailMessage, setActivationEmailMessage] = useState("");
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    localStorage.removeItem("user");
    localStorage.removeItem("tempUser");
  }, []);

  const formatCooldown = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const LoginSchema = Yup.object({
    emailOrId: Yup.string().required(t("required")),
    password: Yup.string().required(t("required")),
  });

  const formik = useFormik({
    initialValues: { emailOrId: "", password: "" },
    validationSchema: LoginSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setLoading(true);
      setApiError("");
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/auth/login`,
          {
            username: values.emailOrId,
            password: values.password,
          },
          {
            headers: {
              lang: i18n.language,
            },
          }
        );

        const token = response.data.data.access_token;
        const loginCount = response.data.data.login_count;
        const fetchedUserData = await fetchUserData(token);

        const userData = {
          ...fetchedUserData,
          token,
        };

        login(userData);

        const res = await checkLoginSurvey(token);

        if (loginCount == 5 || loginCount == 7) {
          navigate("/surveys?key=1", { replace: true });
        } else {
          navigate("/", { replace: true });
        }
      } catch (error) {
        if (error.response) {
          if (error.status == 400) {
            setShowSendActivationEmail(true);
          } else {
            setShowSendActivationEmail(false);
          }
          // Server responded with a status outside 2xx
          setApiError(error.response.data.message || t("invalidCredentials"));
        } else if (error.request) {
          // Request was made but no response
          setApiError(t("networkError") || "Network error. Please try again.");
        } else {
          setApiError(error.message);
        }
      } finally {
        setSubmitting(false);
        setLoading(false);
      }
    },
  });

  const checkLoginSurvey = async (token) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/check-login-survey`, {
        headers: {
          lang: i18n.language,
          Authorization: `Bearer ${token}`,
        },
      });
      return response;
    } catch (err) {
      console.log("Error checking login survey", err);
    }
  };

  const handleSendActivationEmail = async () => {
    if (cooldown > 0) return; // prevent if cooldown active

    setSendingActivationEmail(true);
    setActivationEmailMessage("");

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/auth/resend-verification-email`,
        {
          params: { email: formik.values.emailOrId },
          headers: { lang: i18n.language },
        }
      );

      setActivationEmailMessage(
        t("activationEmailSentSuccessfully") || "Activation email sent successfully."
      );

      // Start cooldown for 30 seconds (adjust as you like)
      setCooldown(300);
    } catch (error) {
      console.log("error", error);
      setActivationEmailMessage(
        t("failedToSendActivationEmail") || "Failed to send activation email. Please try again."
      );
    } finally {
      setSendingActivationEmail(false);
    }
  };

  useEffect(() => {
    if (cooldown === 0) return;

    const timer = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldown]);

  const fetchUserData = async (token) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/auth/me`, {
        headers: {
          lang: i18n.language,
          Authorization: `Bearer ${token}`,
        },
      });

      return response?.data?.data;
    } catch (error) {
      console.error("Error fetching user data:", error);
      setApiError("Failed to load user data. Please try again later.");
      return null;
    }
  };

  return (
    <>
      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/+962790488840" // replace with your number in international format
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-24 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-green-500 text-white shadow-lg transition-transform hover:scale-110 hover:shadow-xl md:bottom-5"
      >
        <FaWhatsapp size={28} />
      </a>
      <div
        className="flex min-h-screen flex-col bg-gray-100 text-sm text-[#616262]" // light gray background
        dir={i18n.language === "ar" ? "rtl" : "ltr"}
      >
        {/* Top bar */}
        <div className="border-b border-gray-200 bg-white py-4">
          <div className="z-20 mx-auto flex w-full max-w-6xl items-center justify-between px-6">
            <div className="flex w-full items-center justify-between gap-4 sm:w-auto sm:justify-start">
              {/* Logo */}
              <img
                onClick={() => navigate("/auth/landing")}
                src="/logo-blue.svg"
                alt="Logo"
                className="h-10 w-auto cursor-pointer sm:h-8 ltr:pr-3 rtl:pl-3"
              />
            </div>
            {/* <LanguageSwitcher defaultTheme /> */}
          </div>
        </div>

        {/* Centered form */}
        <div className="flex flex-1 items-center justify-center p-6">
          <div className="w-full max-w-[30rem] rounded-xl border border-gray-200 bg-white p-8 shadow-lg">
            <p className="text-center text-2xl font-bold text-[#616262]">{t("welcome")}</p>
            <h2 className="mb-6 text-center text-lg font-semibold text-[#616262]">{t("loginMessage")}</h2>

            {/* API Error Message */}
            {apiError && (
              <div className="mb-4 rounded-lg border border-red-500 bg-red-50 p-5 text-red-700">
                <p className={`${sendActivationEmail && "mb-3"} text-sm font-medium`}>{apiError}</p>

                {sendActivationEmail && (
                  <div className="flex flex-col gap-2">
                    <button
                      type="button"
                      onClick={handleSendActivationEmail}
                      disabled={sendingActivationEmail || cooldown > 0}
                      className="w-full rounded bg-blue-600 px-5 py-2 font-semibold text-white transition-colors duration-200 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {sendingActivationEmail
                        ? t("sending...")
                        : cooldown > 0
                          ? t("sendActivationEmailCooldown", { time: formatCooldown(cooldown) })
                          : // e.g. "Send again in 25s"
                            t("sendActivationEmail")}
                    </button>

                    {activationEmailMessage && (
                      <p className="mt-1 text-sm text-green-600">{activationEmailMessage}</p>
                    )}
                  </div>
                )}
              </div>
            )}

            <form onSubmit={formik.handleSubmit} className="flex flex-col gap-5">
              {/* Email / ID */}
              <div className="relative">
                <FaEnvelope className="absolute top-[18px] text-[#616262] ltr:left-3 rtl:right-3" />
                <input
                  type="text"
                  name="emailOrId"
                  placeholder={t("emailOrIdPlaceholder")}
                  className={`w-full rounded-xl border px-10 py-3 text-sm text-[#616262] transition-all duration-300 placeholder:text-[#616262] focus:outline-none focus:ring-purple-500 focus:ring-offset-1 ${
                    formik.touched.emailOrId && formik.errors.emailOrId
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:border-purple-500 focus:ring-purple-300"
                  }`}
                  value={formik.values.emailOrId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>

              {/* Password */}
              <div className="relative">
                <FaLock className="absolute top-4 text-[#616262] ltr:left-3 rtl:right-3" />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder={t("passwordPlaceholder")}
                  className={`w-full rounded-xl border px-10 py-3 text-sm text-[#616262] placeholder:text-[#616262] focus:outline-none focus:ring-purple-500 focus:ring-offset-1 ${
                    formik.touched.password && formik.errors.password
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:border-purple-500 focus:ring-purple-300"
                  }`}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute top-4 text-[#616262] ltr:right-3 rtl:left-3"
                >
                  {showPassword ? <FaEye /> : <FaEyeSlash />}
                </button>
              </div>

              <div>
                <button
                  type="button"
                  onClick={() => navigate("/auth/forgot-password")}
                  className="text-base font-semibold text-[#616262] hover:underline"
                >
                  {t("forgotPassword")}
                </button>
              </div>

              <button
                type="submit"
                disabled={loading || formik.isSubmitting}
                className="w-full rounded-full bg-purple-500 py-3 text-base font-semibold text-white shadow-lg transition-transform duration-300 hover:-translate-y-1 hover:scale-[1.02] hover:bg-purple-600"
              >
                {loading ? (
                  <div className="flex flex-col items-center justify-center">
                    <div className="h-6 w-6 animate-spin rounded-full border-4 border-gray-200 border-t-purple-600"></div>
                  </div>
                ) : (
                  t("login")
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => navigate("/auth/register")}
                className="text-base font-semibold text-[#616262] hover:underline"
              >
                {t("createAccount")}
              </button>
            </div>
          </div>
        </div>

        {/* Footer Logos */}
        <footer dir="ltr" className="mt-auto border-t border-gray-200 bg-white py-1">
          <div className="flex flex-wrap items-center justify-center gap-6">
            <a href="https://modee.gov.jo" target="_blank" rel="noopener noreferrer">
              <img
                src="/Modee2.png"
                alt="Modee"
                className="h-16 w-[75px] object-contain opacity-90 transition hover:opacity-100 sm:h-20"
              />
            </a>

            <a href="https://cpf.jo" target="_blank" rel="noopener noreferrer">
              <img
                src="/CPF2.png"
                alt="CPF"
                className="h-20 w-20 object-contain opacity-90 transition hover:opacity-100 sm:h-20"
              />
            </a>
            <a href="https://ytj.jo" target="_blank" rel="noopener noreferrer">
              <img
                src="/YTJ2.png"
                alt="YTJ"
                className="h-20 w-20 object-contain opacity-90 transition hover:opacity-100 sm:h-20"
              />
            </a>
          </div>
          <div>
            <p className="text-center text-sm text-gray-600">
              &copy; {new Date().getFullYear()} Designed and developed by HWK
            </p>
          </div>
        </footer>
      </div>
    </>
  );
}
